import { useEffect } from 'react';

/**
 * Adds `.in-view` to every `.reveal` element as it enters the viewport.
 * Also watches the DOM for `.reveal` nodes mounted after the first render
 * (accordion bodies, hot reloads, lazily rendered sections) so nothing
 * is ever left stuck at opacity 0.
 */
export function useScrollReveal() {
  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in-view');
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );

    const observe = (root) => {
      const nodes = root.matches?.('.reveal') ? [root] : [];
      root.querySelectorAll?.('.reveal').forEach((n) => nodes.push(n));
      nodes.forEach((n) => {
        if (n.classList.contains('in-view')) return;
        if (reduced) n.classList.add('in-view');
        else io.observe(n);
      });
    };

    observe(document.body);

    const mo = new MutationObserver((mutations) => {
      mutations.forEach((m) =>
        m.addedNodes.forEach((n) => {
          if (n.nodeType === 1) observe(n);
        })
      );
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, []);
}
